// src/context/AppContext.jsx - Profound Philosophy Global App Context with Firebase Integration
// Manages global app state with automatic Firebase sync for multi-device support

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import {
  saveAssessmentResults,
  saveModuleProgress,
  saveToolData,
  saveToolUsage,
  loadAllUserData,
  migrateOldLocalStorage,
  clearCloudData,
  clearLocalData,
  TOOL_STORAGE_KEYS
} from '../services/storageService';
import { useGamificationStore } from '../stores/gamificationStore';
import { PREFERENCES_KEY, setAnalyticsConsent } from '../services/analytics';

const AppContext = createContext({});

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const { user, storagePreference } = useAuth();
  const [loading, setLoading] = useState(true);
  const [assessmentResults, setAssessmentResultsState] = useState(null);
  const [moduleProgress, setModuleProgressState] = useState({});
  const [toolsUsage, setToolsUsageState] = useState({});
  const [toolData, setToolDataState] = useState({});
  const [theme, setTheme] = useState('dark');
  const [notifications, setNotifications] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Load user data when user logs in or changes
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.uid) {
        // Signed out: hydrate everything this device already holds. The app
        // advertises "no signup required", and every one of these is written
        // to localStorage as you go — but until this ran, only the assessment
        // was ever read back, so a signed-out reader lost their place in a
        // module (and their tool history) on every reload.
        try {
          const localAssessment = localStorage.getItem('profoundPhilosophy_assessmentResults');
          if (localAssessment) {
            setAssessmentResultsState(JSON.parse(localAssessment));
          }
        } catch (e) { /* corrupt local data — ignore */ }
        try {
          const localProgress = JSON.parse(localStorage.getItem('profoundPhilosophy_moduleProgress'));
          if (localProgress && Object.keys(localProgress).length > 0) {
            setModuleProgressState(localProgress);
          }
        } catch (e) { /* corrupt local data — ignore */ }
        try {
          const localToolData = JSON.parse(localStorage.getItem('profoundPhilosophy_toolData'));
          if (localToolData && Object.keys(localToolData).length > 0) {
            setToolDataState(localToolData);
          }
        } catch (e) { /* corrupt local data — ignore */ }
        try {
          const localToolUsage = JSON.parse(localStorage.getItem('profoundPhilosophy_toolUsage'));
          if (localToolUsage && Object.keys(localToolUsage).length > 0) {
            setToolsUsageState(localToolUsage);
          }
        } catch (e) { /* corrupt local data — ignore */ }
        setLoading(false);
        setDataLoaded(false);
        return;
      }

      setLoading(true);

      try {
        // Migrate old localStorage keys first
        migrateOldLocalStorage();

        console.log('📥 Loading user data for:', user.uid);

        // Load all user data from Firebase/localStorage
        const data = await loadAllUserData(user.uid, storagePreference);

        // Update state with loaded data
        if (data.assessmentResults) {
          setAssessmentResultsState(data.assessmentResults);
        }
        if (data.moduleProgress && Object.keys(data.moduleProgress).length > 0) {
          setModuleProgressState(data.moduleProgress);
        }
        if (data.toolData && Object.keys(data.toolData).length > 0) {
          setToolDataState(data.toolData);
        }
        if (data.toolUsage && Object.keys(data.toolUsage).length > 0) {
          setToolsUsageState(data.toolUsage);
        }

        setDataLoaded(true);
        console.log('✅ User data loaded successfully');
      } catch (error) {
        console.error('❌ Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user?.uid, storagePreference]);

  // Wrapper function to save assessment results
  const setAssessmentResults = async (results) => {
    setAssessmentResultsState(results);
    if (!results) return;

    if (user?.uid) {
      await saveAssessmentResults(user.uid, results, storagePreference);
    } else {
      // Signed out: keep the result on this device (cloud copy attaches on
      // next sign-in via the normal save path). 'local' never touches Firestore.
      await saveAssessmentResults(null, results, 'local');
    }
  };

  // Module progress helper functions with auto-save
  const getModuleProgress = (moduleId) => {
    return moduleProgress?.[moduleId] || { completed: false, started: false };
  };

  const updateModuleProgress = async (moduleId, progressData) => {
    const updatedProgress = {
      ...moduleProgress,
      [moduleId]: {
        ...moduleProgress?.[moduleId],
        ...progressData,
        updatedAt: new Date().toISOString()
      }
    };

    setModuleProgressState(updatedProgress);

    // Save to Firebase if user is logged in
    if (user?.uid) {
      await saveModuleProgress(user.uid, moduleId, updatedProgress[moduleId], storagePreference);
    }
  };

  const completeModule = async (moduleId) => {
    const completionData = {
      ...moduleProgress?.[moduleId],
      completed: true,
      completedAt: new Date().toISOString(),
      progress: 100
    };

    const updatedProgress = {
      ...moduleProgress,
      [moduleId]: completionData
    };

    setModuleProgressState(updatedProgress);

    // Save to Firebase if user is logged in
    if (user?.uid) {
      await saveModuleProgress(user.uid, moduleId, completionData, storagePreference);
    }
  };

  const isModuleCompleted = (moduleId) => {
    return moduleProgress?.[moduleId]?.completed || false;
  };

  // Tool data helpers with auto-save
  const updateToolData = async (toolId, data) => {
    const updatedData = {
      ...toolData,
      [toolId]: {
        ...toolData?.[toolId],
        ...data,
        updatedAt: new Date().toISOString()
      }
    };

    setToolDataState(updatedData);

    // Save to Firebase if user is logged in
    if (user?.uid) {
      await saveToolData(user.uid, toolId, updatedData[toolId], storagePreference);
    }
  };

  const updateToolUsage = async (toolId, usage) => {
    const isFirstUse = !toolsUsage?.[toolId];

    const updatedUsage = {
      ...toolsUsage,
      [toolId]: {
        ...toolsUsage?.[toolId],
        ...usage,
        updatedAt: new Date().toISOString()
      }
    };

    setToolsUsageState(updatedUsage);

    // Award gamification points for tool usage
    const gamification = useGamificationStore.getState();
    if (isFirstUse) {
      gamification.completeTool(toolId); // First use awards more points
    } else {
      gamification.useToolSession(toolId); // Regular session points
    }

    // Save to Firebase if user is logged in
    if (user?.uid) {
      await saveToolUsage(user.uid, toolId, updatedUsage[toolId], storagePreference);
    }
  };

  // Privacy preferences, export and reset — backing the Data & Privacy page.
  // Preferences are device-local by design: they govern whether anything syncs
  // at all, so they can't live behind the sync they control.
  const DEFAULT_PREFERENCES = {
    cloudSync: false,
    analytics: false,
    progressTracking: true,
    dataRetentionPeriod: 'indefinite',
    encryptionLevel: 'maximum'
  };

  const getPreferences = async () => {
    try {
      const stored = localStorage.getItem(PREFERENCES_KEY);
      return stored
        ? { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) }
        : { ...DEFAULT_PREFERENCES };
    } catch (error) {
      console.error('Error reading preferences:', error);
      return { ...DEFAULT_PREFERENCES };
    }
  };

  const updatePreferences = async (newPreferences) => {
    // Merge over what's stored, not the defaults, so a partial update (the
    // consent notice passes only { analytics }) can't reset other settings.
    const merged = { ...(await getPreferences()), ...newPreferences };
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(merged));
    // Consent takes effect immediately, not on next load: switching on injects
    // the GA4 loader now, switching off stops collection for this session too.
    setAnalyticsConsent(merged.analytics === true);
    return merged;
  };

  const exportUserData = async () => {
    const localEntries = {};
    // The export promises everything: the app's own keys plus the journal,
    // the positions and every tool's saved reflections, which use their own
    // keys (the same list Clear All Data removes).
    Object.keys(localStorage)
      .filter(key => key.startsWith('profoundPhilosophy_') || TOOL_STORAGE_KEYS.includes(key))
      .forEach(key => {
        const raw = localStorage.getItem(key);
        try {
          localEntries[key] = JSON.parse(raw);
        } catch {
          localEntries[key] = raw;
        }
      });

    return {
      app: 'Profound Philosophy',
      exportedAt: new Date().toISOString(),
      userId: user?.uid || null,
      assessmentResults,
      moduleProgress,
      toolData,
      toolUsage: toolsUsage,
      preferences: await getPreferences(),
      localStorage: localEntries
    };
  };

  const resetProgress = async (confirmed = false) => {
    if (!confirmed) return false;

    // Everything this app keeps on the device: the profoundPhilosophy_ keys
    // and every tool's, the journal's, the positions' and the points store's
    // own key. Prefix-only clearing left a reader's reflections behind.
    clearLocalData();
    const gamification = useGamificationStore;
    gamification.persist?.clearStorage();
    if (gamification.getInitialState) gamification.setState(gamification.getInitialState(), true);

    if (user?.uid) {
      try {
        await clearCloudData(user.uid);
      } catch (error) {
        console.error('Error clearing cloud data:', error);
      }
    }

    setAssessmentResultsState(null);
    setModuleProgressState({});
    setToolDataState({});
    setToolsUsageState({});

    return true;
  };

  const value = {
    user,
    loading,
    dataLoaded,

    // Assessment
    assessmentResults,
    setAssessmentResults,

    // Module Progress
    moduleProgress,
    setModuleProgress: setModuleProgressState, // Direct setter (use updateModuleProgress instead)
    getModuleProgress,
    updateModuleProgress,
    completeModule,
    isModuleCompleted,

    // Tool Data & Usage
    toolData,
    toolsUsage,
    updateToolData,
    updateToolUsage,
    setToolsUsage: setToolsUsageState, // Direct setter (use updateToolUsage instead)

    // Privacy, export and reset
    getPreferences,
    updatePreferences,
    exportUserData,
    resetProgress,

    // UI Settings
    theme,
    setTheme,
    notifications,
    setNotifications
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};